import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api, { dataAPI, interviewAPI } from '../lib/api';
import ReactMarkdown from 'react-markdown';
import {
  GraduationCap, Building2, Loader2, Download, Mic, MicOff, Play, Square,
  ChevronRight, Star, CheckCircle2, XCircle, RotateCcw, Clock, Target,
  Volume2, ArrowLeft, Sparkles, Trophy, MessageSquare, ThumbsUp, AlertTriangle,
  Eye, Shield, Users, TrendingUp, Lightbulb, FileText, Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

// ─── Self Assessment Component ───────────────────────────────────────────
function SelfAssessment({ sessionId, results }) {
  const [confidence, setConfidence] = useState(5);
  const [preparedness, setPreparedness] = useState(5);
  const [weakAreas, setWeakAreas] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const weakOptions = ['Technical knowledge', 'Communication clarity', 'STAR format', 'Confidence', 'Specific examples', 'Time management', 'Domain depth'];

  const toggleWeak = (area) => {
    setWeakAreas(prev => prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await interviewAPI.selfAssessment({
        session_id: sessionId,
        confidence, preparedness, weak_areas: weakAreas, notes
      });
      setAssessmentResult(res.data.self_assessment?.result || res.data.self_assessment);
      toast.success('Self-assessment submitted!');
    } catch { toast.error('Failed to submit assessment'); }
    finally { setSubmitting(false); }
  };

  if (assessmentResult) {
    const r = assessmentResult;
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-5 space-y-4" data-testid="assessment-results">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider text-indigo-400 font-bold flex items-center gap-2"><Brain className="w-4 h-4" /> Improvement Plan</p>
          {r.self_awareness_score && (
            <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 border">
              Self-Awareness: {r.self_awareness_score}/10
            </Badge>
          )}
        </div>
        {r.self_awareness_note && <p className="text-sm" style={{ color: 'var(--t-text-secondary)' }}>{r.self_awareness_note}</p>}
        {r.blind_spots?.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-amber-400/60 font-bold mb-1">Blind Spots</p>
            {r.blind_spots.map((b, i) => <p key={i} className="text-xs text-amber-400/80 flex items-center gap-1"><Eye className="w-3 h-3" /> {b}</p>)}
          </div>
        )}
        {r.improvement_plan?.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-purple-400/60 font-bold mb-2">Improvement Plan</p>
            {r.improvement_plan.map((p, i) => (
              <div key={i} className="flex items-start gap-3 mb-2 rounded-lg p-2" style={{ background: 'var(--t-bg-section)' }}>
                <Target className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--t-text-heading)' }}>{p.area}</p>
                  <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>{p.action}</p>
                  <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{p.timeline}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {r.strengths_to_leverage?.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-emerald-400/60 font-bold mb-1">Leverage These</p>
            {r.strengths_to_leverage.map((s, i) => <p key={i} className="text-xs text-emerald-400/80 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {s}</p>)}
          </div>
        )}
        {r.next_mock_focus && (
          <div className="bg-purple-500/5 rounded-lg p-3 border border-purple-500/10">
            <p className="text-xs uppercase tracking-wider text-purple-400/60 font-bold mb-1">Next Mock Focus</p>
            <p className="text-sm text-purple-300">{r.next_mock_focus}</p>
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }} data-testid="self-assessment-form">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-indigo-400" />
          <span className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Post-Interview Self-Assessment</span>
        </div>
        <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} style={{ color: 'var(--t-text-muted)' }} />
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden">
            <div className="px-4 pb-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold block mb-2" style={{ color: 'var(--t-text-muted)' }}>Confidence Level</label>
                  <div className="flex items-center gap-3">
                    <input type="range" min="1" max="10" value={confidence} onChange={e => setConfidence(Number(e.target.value))}
                      className="flex-1 accent-indigo-500" data-testid="confidence-slider" />
                    <span className="text-lg font-bold text-indigo-400 w-8 text-center" style={{ fontFamily: 'JetBrains Mono' }}>{confidence}</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold block mb-2" style={{ color: 'var(--t-text-muted)' }}>Preparedness</label>
                  <div className="flex items-center gap-3">
                    <input type="range" min="1" max="10" value={preparedness} onChange={e => setPreparedness(Number(e.target.value))}
                      className="flex-1 accent-indigo-500" data-testid="preparedness-slider" />
                    <span className="text-lg font-bold text-indigo-400 w-8 text-center" style={{ fontFamily: 'JetBrains Mono' }}>{preparedness}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold block mb-2" style={{ color: 'var(--t-text-muted)' }}>Where did you feel weak?</label>
                <div className="flex flex-wrap gap-2">
                  {weakOptions.map(area => (
                    <button key={area} onClick={() => toggleWeak(area)} data-testid={`weak-${area.replace(/\s/g, '-').toLowerCase()}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        weakAreas.includes(area) ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' : 'border'
                      }`}
                      style={!weakAreas.includes(area) ? { background: 'var(--t-bg-section)', borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' } : {}}>{area}</button>
                  ))}
                </div>
              </div>
              <textarea data-testid="assessment-notes" value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Any additional reflections on your performance..."
                className="w-full border rounded-lg px-3 py-2 text-sm resize-none h-16 focus:border-indigo-500 focus:outline-none"
                style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
              <Button data-testid="submit-assessment-btn" onClick={submit} disabled={submitting}
                className="w-full bg-indigo-600 text-white hover:bg-indigo-500 rounded-lg h-10 font-bold uppercase tracking-wider text-xs">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : <><Brain className="w-4 h-4 mr-2" /> Get Improvement Plan</>}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Company Intel Component ─────────────────────────────────────────────
function CompanyIntel({ job }) {
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchIntel = async () => {
    if (intel) { setExpanded(!expanded); return; }
    setExpanded(true);
    setLoading(true);
    try {
      const res = await interviewAPI.companyIntel(job.id);
      setIntel(res.data.intel);
    } catch { toast.error('Failed to load company intel'); }
    finally { setLoading(false); }
  };

  const sections = [
    { key: 'overview', icon: Building2, label: 'Overview', color: 'text-blue-400' },
    { key: 'culture', icon: Users, label: 'Culture & Values', color: 'text-emerald-400', isList: true },
    { key: 'recent_news', icon: TrendingUp, label: 'Recent Developments', color: 'text-amber-400', isList: true },
    { key: 'leadership', icon: Shield, label: 'Key Leadership', color: 'text-purple-400', isList: true },
    { key: 'interview_style', icon: MessageSquare, label: 'Interview Style', color: 'text-cyan-400' },
    { key: 'insider_tips', icon: Lightbulb, label: 'Insider Tips', color: 'text-amber-400', isList: true },
    { key: 'red_flags', icon: AlertTriangle, label: 'Red Flags to Avoid', color: 'text-red-400', isList: true },
    { key: 'salary_intel', icon: Target, label: 'Salary Intel', color: 'text-emerald-400' },
  ];

  return (
    <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }} data-testid="company-intel-section">
      <button onClick={fetchIntel} data-testid="company-intel-toggle"
        className="w-full flex items-center justify-between p-4 text-left hover: transition-colors">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-cyan-400" />
          <span className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Company Intel: {job.company}</span>
        </div>
        <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} style={{ color: 'var(--t-text-muted)' }} />
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden">
            <div className="px-4 pb-4">
              {loading ? (
                <div className="flex items-center justify-center py-8 gap-2">
                  <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                  <span className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Gathering company intelligence...</span>
                </div>
              ) : intel ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="company-intel-content">
                  {sections.map(({ key, icon: Icon, label, color, isList }) => {
                    const val = intel[key];
                    if (!val || (Array.isArray(val) && val.length === 0)) return null;
                    return (
                      <div key={key} className="border rounded-lg p-3" style={{ background: 'var(--t-bg-card)', borderColor: 'var(--t-border)' }}>
                        <p className={`text-xs uppercase tracking-wider ${color} font-bold mb-1.5 flex items-center gap-1`}>
                          <Icon className="w-3 h-3" /> {label}
                        </p>
                        {isList ? (
                          Array.isArray(val) && val.map((item, i) => (
                            <p key={i} className="text-xs mb-1 flex items-start gap-1" style={{ color: 'var(--t-text-secondary)' }}>
                              <span className="mt-0.5" style={{ color: 'var(--t-text-muted)' }}>-</span> {item}
                            </p>
                          ))
                        ) : (
                          <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{val}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Mock Interview Component ────────────────────────────────────────────
function MockInterview({ job, onBack }) {
  const [phase, setPhase] = useState('setup'); // setup, interview, feedback, results
  const [questionType, setQuestionType] = useState('mixed');
  const [questionCount, setQuestionCount] = useState(5);
  const [session, setSession] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(null);
  const [allFeedback, setAllFeedback] = useState([]);
  const [playingAudio, setPlayingAudio] = useState(false);
  const [results, setResults] = useState(null);
  const [textAnswer, setTextAnswer] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  const startInterview = async () => {
    setStarting(true);
    try {
      const res = await interviewAPI.startMock(job.id, questionType, questionCount, job.role, job.company);
      setSession(res.data);
      setCurrentQ(0);
      setAllFeedback([]);
      setPhase('interview');
      toast.success('Mock interview started!');
    } catch (err) {
      toast.error('Failed to start mock interview. Try again.');
    } finally { setStarting(false); }
  };

  const playQuestion = async (text) => {
    setPlayingAudio(true);
    try {
      const res = await interviewAPI.tts(text);
      const audio = new Audio(`data:audio/mp3;base64,${res.data.audio_base64}`);
      audioRef.current = audio;
      audio.onended = () => setPlayingAudio(false);
      audio.play();
    } catch {
      setPlayingAudio(false);
      toast.error('Voice playback unavailable. Read the question on screen.');
    }
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingAudio(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.start();
      setRecording(true);
    } catch {
      toast.error('Microphone access denied. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (mediaRecorderRef.current && recording) {
        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
          resolve(blob);
        };
        mediaRecorderRef.current.stop();
        setRecording(false);
      } else { resolve(null); }
    });
  };

  const submitAnswer = async (audioBlob, textInput) => {
    if (!session) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('session_id', session.session_id);
      formData.append('question_index', currentQ);
      if (audioBlob) {
        formData.append('audio', audioBlob, 'answer.webm');
      }
      if (textInput) {
        formData.append('text_answer', textInput);
      }
      const res = await interviewAPI.submitAnswer(formData);
      const fb = { ...res.data, questionIndex: currentQ, question: session.questions[currentQ].question };
      setCurrentFeedback(fb);
      setAllFeedback(prev => [...prev, fb]);
      setPhase('feedback');
    } catch (err) {
      toast.error('Failed to process answer. Try again.');
    } finally { setSubmitting(false); }
  };

  const handleStopAndSubmit = async () => {
    const blob = await stopRecording();
    if (blob) await submitAnswer(blob, null);
  };

  const handleTextSubmit = async () => {
    if (!textAnswer.trim()) return;
    await submitAnswer(null, textAnswer.trim());
    setTextAnswer('');
  };

  const nextQuestion = () => {
    setCurrentFeedback(null);
    setTextAnswer('');
    if (currentQ + 1 < session.questions.length) {
      setCurrentQ(currentQ + 1);
      setPhase('interview');
    } else {
      finishInterview();
    }
  };

  const finishInterview = async () => {
    try {
      const res = await interviewAPI.completeMock(session.session_id);
      setResults(res.data);
      setPhase('results');
    } catch { toast.error('Failed to complete interview'); }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-emerald-400';
    if (score >= 6) return 'text-amber-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 8) return 'bg-emerald-500/10 border-emerald-500/20';
    if (score >= 6) return 'bg-[#0D9488]/10 border-[#0D9488]/20';
    return 'bg-red-500/10 border-red-500/20';
  };

  // ─── SETUP PHASE ──────────────────────────────────────────
  if (phase === 'setup') {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <button onClick={onBack} className="flex items-center gap-2 text-sm hover:text-[#0D9488] transition-colors" style={{ color: 'var(--t-text-muted)' }} data-testid="mock-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Interview Prep
        </button>
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center mx-auto">
            <Mic className="w-8 h-8 text-purple-400" />
          </div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--t-text-heading)' }}>Voice Mock Interview</h2>
          <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>{job.company} | {job.role}</p>
        </div>

        <div className="max-w-md mx-auto space-y-6">
          <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-3 block" style={{ color: 'var(--t-text-muted)' }}>Question Type</label>
            <div className="grid grid-cols-3 gap-2">
              {['technical', 'behavioral', 'mixed'].map(t => (
                <button key={t} data-testid={`mock-type-${t}`}
                  onClick={() => setQuestionType(t)}
                  className={`px-4 py-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                    questionType === t
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                      : 'border'
                  }`}
                  style={questionType !== t ? { background: 'var(--t-bg-section)', borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' } : {}}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-3 block" style={{ color: 'var(--t-text-muted)' }}>Number of Questions</label>
            <div className="grid grid-cols-3 gap-2">
              {[3, 5, 8].map(n => (
                <button key={n} data-testid={`mock-count-${n}`}
                  onClick={() => setQuestionCount(n)}
                  className={`px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                    questionCount === n
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                      : 'border'
                  }`}
                  style={questionCount !== n ? { background: 'var(--t-bg-section)', borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' } : {}}>{n} Questions</button>
              ))}
            </div>
          </div>
          <Button data-testid="start-mock-btn" onClick={startInterview} disabled={starting}
            className="w-full bg-purple-600 text-white hover:bg-purple-500 rounded-lg h-12 font-bold uppercase tracking-wider text-sm">
            {starting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Questions...</> : <><Mic className="w-4 h-4 mr-2" /> Start Mock Interview</>}
          </Button>
        </div>
      </motion.div>
    );
  }

  // ─── INTERVIEW PHASE ──────────────────────────────────────
  if (phase === 'interview' && session) {
    const q = session.questions[currentQ];
    return (
      <motion.div key={`q-${currentQ}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 border">{q.type}</Badge>
            <span className="text-xs" style={{ color: 'var(--t-text-muted)' }}>Question {currentQ + 1} of {session.total}</span>
          </div>
          <Progress value={((currentQ + 1) / session.total) * 100} className="w-32 h-1.5" />
        </div>

        <div className="border rounded-xl p-6 space-y-4" style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}>
          <div className="flex items-start justify-between gap-4">
            <p className="text-lg font-medium leading-relaxed" style={{ color: 'var(--t-text-heading)' }} data-testid={`mock-question-${currentQ}`}>{q.question}</p>
            <button onClick={() => playingAudio ? stopAudio() : playQuestion(q.question)}
              className={`p-2 rounded-lg flex-shrink-0 transition-colors ${playingAudio ? 'bg-purple-500/20 text-purple-400' : ''}`}
              style={!playingAudio ? { background: 'var(--t-bg-card)', color: 'var(--t-text-muted)' } : {}}
              data-testid="play-question-btn">
              {playingAudio ? <Square className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="text-center space-y-4">
          {!recording && !submitting && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <Button data-testid="start-recording-btn" onClick={startRecording}
                    className="bg-red-600 text-white hover:bg-red-500 rounded-full h-16 w-16">
                    <Mic className="w-6 h-6" />
                  </Button>
                  <p className="text-xs mt-2" style={{ color: 'var(--t-text-muted)' }}>Record Answer</p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-800"></div></div>
                <div className="relative flex justify-center"><span className="px-3 text-xs uppercase" style={{ background: 'var(--t-bg)', color: 'var(--t-text-muted)' }}>or type</span></div>
              </div>
              <div className="flex gap-2 max-w-lg mx-auto">
                <textarea data-testid="text-answer-input" value={textAnswer} onChange={e => setTextAnswer(e.target.value)}
                  placeholder="Type your answer here..."
                  className="flex-1 border rounded-lg px-3 py-2 text-sm resize-none min-h-[60px] focus:border-purple-500 focus:outline-none"
                  style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
                <Button data-testid="submit-text-btn" onClick={handleTextSubmit} disabled={!textAnswer.trim()}
                  className="bg-purple-600 text-white hover:bg-purple-500 rounded-lg px-4 self-end">
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          )}
          {recording && (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-sm text-red-400 font-medium">Recording...</span>
              </div>
              <Button data-testid="stop-recording-btn" onClick={handleStopAndSubmit}
                className="bg-[#0D9488] text-black hover:bg-[#D4710F] rounded-full h-16 w-16 mx-auto">
                <Square className="w-6 h-6" />
              </Button>
              <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>Click to stop and submit</p>
            </div>
          )}
          {submitting && (
            <div className="space-y-3">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto" />
              <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Analyzing your answer...</p>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // ─── FEEDBACK PHASE ───────────────────────────────────────
  if (phase === 'feedback' && currentFeedback) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Question {currentQ + 1} of {session.total}</span>
          <Progress value={((currentQ + 1) / session.total) * 100} className="w-32 h-1.5" />
        </div>

        <div className={`rounded-xl border p-6 ${getScoreBg(currentFeedback.score)}`}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-wider font-bold" style={{ color: 'var(--t-text-muted)' }}>Your Score</p>
            <span className={`text-4xl font-black ${getScoreColor(currentFeedback.score)}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {currentFeedback.score}/10
            </span>
          </div>

          <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'var(--t-text-muted)' }}>Your Answer</p>
          <p className="text-sm mb-4 rounded-lg p-3 italic" style={{ color: 'var(--t-text-secondary)', background: 'var(--t-bg-section)' }}>"{currentFeedback.transcription}"</p>

          <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'var(--t-text-muted)' }}>Feedback</p>
          <p className="text-sm mb-4" style={{ color: 'var(--t-text-secondary)' }}>{currentFeedback.feedback}</p>

          {currentFeedback.strengths?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs uppercase tracking-wider text-emerald-400/60 font-bold mb-1 flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> Strengths</p>
              {currentFeedback.strengths.map((s, i) => (
                <p key={i} className="text-xs text-emerald-400/80 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {s}</p>
              ))}
            </div>
          )}
          {currentFeedback.improvements?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs uppercase tracking-wider text-amber-400/60 font-bold mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> To Improve</p>
              {currentFeedback.improvements.map((s, i) => (
                <p key={i} className="text-xs text-amber-400/80 flex items-center gap-1"><XCircle className="w-3 h-3" /> {s}</p>
              ))}
            </div>
          )}
          {currentFeedback.suggested_answer && (
            <div>
              <p className="text-xs uppercase tracking-wider text-blue-400/60 font-bold mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Suggested Answer</p>
              <p className="text-xs text-blue-400/80 bg-blue-500/5 rounded-lg p-2">{currentFeedback.suggested_answer}</p>
            </div>
          )}
        </div>

        <Button data-testid="next-question-btn" onClick={nextQuestion}
          className="w-full bg-purple-600 text-white hover:bg-purple-500 rounded-lg h-11 font-bold uppercase tracking-wider text-xs">
          {currentQ + 1 < session.total ? <><ChevronRight className="w-4 h-4 mr-2" /> Next Question</> : <><Trophy className="w-4 h-4 mr-2" /> View Results</>}
        </Button>
      </motion.div>
    );
  }

  // ─── RESULTS PHASE ────────────────────────────────────────
  if (phase === 'results' && results) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <div className="text-center space-y-3">
          <Trophy className="w-12 h-12 text-[#0D9488] mx-auto" />
          <h2 className="text-2xl font-bold" style={{ color: 'var(--t-text-heading)' }}>Interview Complete!</h2>
          <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>{results.company} | {results.role}</p>
          <div className={`inline-block rounded-xl border px-8 py-4 ${getScoreBg(results.overall_score)}`}>
            <p className="text-xs uppercase tracking-wider font-bold" style={{ color: 'var(--t-text-muted)' }}>Overall Score</p>
            <span className={`text-5xl font-black ${getScoreColor(results.overall_score)}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {results.overall_score}/10
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {results.top_strengths?.length > 0 && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
              <p className="text-xs uppercase tracking-wider text-emerald-400 font-bold mb-2">Top Strengths</p>
              {results.top_strengths.map((s, i) => (
                <p key={i} className="text-sm text-emerald-400/80 flex items-center gap-2 mb-1"><CheckCircle2 className="w-3 h-3 flex-shrink-0" /> {s}</p>
              ))}
            </div>
          )}
          {results.key_improvements?.length > 0 && (
            <div className="bg-[#0D9488]/5 border border-[#0D9488]/20 rounded-xl p-4">
              <p className="text-xs uppercase tracking-wider text-amber-400 font-bold mb-2">Key Improvements</p>
              {results.key_improvements.map((s, i) => (
                <p key={i} className="text-sm text-amber-400/80 flex items-center gap-2 mb-1"><AlertTriangle className="w-3 h-3 flex-shrink-0" /> {s}</p>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider font-bold" style={{ color: 'var(--t-text-muted)' }}>Question-by-Question Breakdown</p>
          {results.answers?.map((a, i) => (
            <div key={i} className={`rounded-lg border p-3 ${getScoreBg(a.score)}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm flex-1" style={{ color: 'var(--t-text-secondary)' }}>Q{a.question_index + 1}: {session?.questions?.[a.question_index]?.question?.substring(0, 80)}...</p>
                <span className={`text-lg font-bold ${getScoreColor(a.score)}`}>{a.score}/10</span>
              </div>
            </div>
          ))}
        </div>

        {/* Self-Assessment Section */}
        <SelfAssessment sessionId={session?.session_id} results={results} />

        <div className="flex gap-3">
          <Button data-testid="retake-mock-btn" onClick={() => { setPhase('setup'); setSession(null); setResults(null); setAllFeedback([]); }}
            className="flex-1 bg-purple-600/20 text-purple-400 border border-purple-500/30 hover:bg-purple-600/30 rounded-lg h-11 font-bold uppercase tracking-wider text-xs">
            <RotateCcw className="w-4 h-4 mr-2" /> Take Another
          </Button>
          <Button data-testid="back-to-prep-btn" onClick={onBack}
            className="flex-1 rounded-lg h-11 font-bold uppercase tracking-wider text-xs"
            style={{ background: 'var(--t-bg-card)', color: 'var(--t-text-secondary)', border: '1px solid var(--t-border)' }}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Prep
          </Button>
        </div>
      </motion.div>
    );
  }

  return null;
}

// ─── Main Interview Prep Page ────────────────────────────────────────────
export default function InterviewPrepPage() {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [prepContent, setPrepContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [mockMode, setMockMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [customRole, setCustomRole] = useState('');
  const [customCompany, setCustomCompany] = useState('');
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // Load user profile to generate interview targets from their data
        const [profileRes, historyRes] = await Promise.all([
          dataAPI.profile(),
          interviewAPI.history()
        ]);
        const profile = profileRes.data;
        setHistory(historyRes.data || []);
        setProfileLoaded(true);

        // Build interview targets from user's profile data
        const targets = [];
        if (profile?.title) {
          targets.push({
            id: 'current-role',
            role: profile.title,
            company: profile.current_company || 'Your Current Company',
            fit_score: 90,
          });
        }
        // Add targets from CI sessions if available
        try {
          const sessRes = await api.get('/kairera/sessions');
          const sessions = sessRes?.data?.sessions || [];
          sessions.forEach((s, i) => {
            if (s.pathway_label && s.pathway !== 'start_career') {
              targets.push({
                id: `ci-${i}`,
                role: s.pathway_label,
                company: 'Career Goal',
                fit_score: Math.round(s.completion_pct || 50),
              });
            }
          });
        } catch {}

        // Add a "Senior" version of their current role
        if (profile?.title) {
          targets.push({
            id: 'next-level',
            role: `Senior ${profile.title}`,
            company: profile.current_company || 'Next Level',
            fit_score: 75,
          });
        }

        // Fallback targets if no profile data
        if (targets.length === 0) {
          targets.push(
            { id: 'generic-1', role: 'Software Engineer', company: 'Tech Company', fit_score: 70 },
            { id: 'generic-2', role: 'Product Manager', company: 'Tech Company', fit_score: 65 },
          );
        }

        setJobs(targets);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const generatePrep = async (j) => {
    setGenerating(true);
    setPrepContent(null);
    try {
      const res = await interviewAPI.generatePrep(j.id, 'general', j.role, j.company);
      setPrepContent(res.data);
    } catch {
      toast.error('Failed to generate interview prep. Try again.');
    } finally { setGenerating(false); }
  };

  const handleSelectJob = (job) => {
    setSelectedJob(job);
    setMockMode(false);
    setPrepContent(null);
    generatePrep(job);
  };

  const refreshHistory = async () => {
    try {
      const res = await interviewAPI.history();
      setHistory(res.data);
    } catch {}
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-emerald-400';
    if (score >= 6) return 'text-amber-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Mock Interview mode
  if (mockMode && selectedJob) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="mock-interview-page">
        <MockInterview
          job={selectedJob}
          onBack={() => { setMockMode(false); refreshHistory(); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="interview-prep-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-purple-500/80 font-bold mb-2">Interview Intelligence</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-none" style={{ color: 'var(--t-text-heading)' }}>
            Interview Prep
          </h1>
          <p className="mt-2 text-sm" style={{ color: 'var(--t-text-muted)' }}>
            Smart prep & voice mock interviews tailored to each company
          </p>
        </div>
        <Button data-testid="toggle-history-btn" variant="outline" onClick={() => setShowHistory(!showHistory)}
          className="border text-xs"
          style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)', color: 'var(--t-text-secondary)' }}>
          <Clock className="w-4 h-4 mr-2" /> Mock History ({history.filter(h => h.status === 'completed').length})
        </Button>
      </div>

      {/* History Panel */}
      <AnimatePresence>
        {showHistory && history.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden">
            <div className="border rounded-xl p-4 space-y-2" style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }} data-testid="mock-history-panel">
              <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: 'var(--t-text-muted)' }}>Past Mock Interviews</p>
              {history.filter(h => h.status === 'completed').map((h, i) => (
                <div key={h.id || i} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'var(--t-bg-card)', border: '1px solid var(--t-border)' }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--t-text-heading)' }}>{h.role}</p>
                    <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>{h.company} · {h.question_type} · {new Date(h.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xl font-bold ${getScoreColor(h.overall_score || 0)}`}>{h.overall_score || '-'}/10</span>
                    <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{h.answers?.length || 0}/{h.questions?.length || 0} answered</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Role Input */}
      <div className="nx-card p-4" data-testid="custom-role-section">
        <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#0D9488' }}>
          Practice for a Specific Role
        </p>
        <div className="flex gap-2">
          <input type="text" value={customRole} onChange={e => setCustomRole(e.target.value)}
            placeholder="Enter a role (e.g. Product Manager)" className="flex-1 h-9 px-3 rounded-lg text-sm border"
            style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
            data-testid="custom-role-input" />
          <input type="text" value={customCompany} onChange={e => setCustomCompany(e.target.value)}
            placeholder="Company (optional)" className="w-40 h-9 px-3 rounded-lg text-sm border"
            style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
            data-testid="custom-company-input" />
          <Button disabled={!customRole.trim()} data-testid="custom-prep-btn"
            onClick={() => {
              const custom = { id: `custom-${Date.now()}`, role: customRole, company: customCompany || 'Any Company', fit_score: 80 };
              setJobs(prev => [custom, ...prev]);
              handleSelectJob(custom);
              setCustomRole(''); setCustomCompany('');
            }}
            className="h-9 px-4 text-xs font-semibold rounded-lg" style={{ background: '#0D9488', color: '#fff' }}>
            <Play className="w-3.5 h-3.5 mr-1" /> Prepare
          </Button>
        </div>
      </div>

      {/* Job Selector */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {jobs.map((job) => (
          <motion.button key={job.id} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
            data-testid={`select-job-${job.id}`}
            onClick={() => handleSelectJob(job)}
            className={`text-left p-4 rounded-xl border transition-all ${
              selectedJob?.id === job.id
                ? 'bg-purple-500/10 border-purple-500/30'
                : 'hover:border-slate-700'
            }`}
            style={selectedJob?.id !== job.id ? { background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' } : {}}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--t-bg-card)' }}>
                <Building2 className="w-5 h-5" style={{ color: 'var(--t-text-muted)' }} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--t-text-heading)' }}>{job.role}</p>
                <p className="text-xs truncate" style={{ color: 'var(--t-text-muted)' }}>{job.company}</p>
              </div>
              <span className="text-lg font-bold text-purple-400 ml-auto" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{job.fit_score}%</span>
            </div>
          </motion.button>
        ))}
      </div>

      {/* Interview Prep Content */}
      {selectedJob && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="border rounded-xl overflow-hidden" style={{ background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }} data-testid="prep-content-section">
          <div className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" style={{ borderBottom: '1px solid var(--t-border)' }}>
            <div>
              <h2 className="text-xl font-bold" style={{ color: 'var(--t-text-heading)' }}>{selectedJob.role}</h2>
              <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>{selectedJob.company}</p>
            </div>
            <div className="flex gap-2">
              <Button data-testid="start-mock-interview-btn" onClick={() => setMockMode(true)}
                className="bg-purple-600 text-white hover:bg-purple-500 rounded-lg h-10 px-5 font-bold uppercase tracking-wider text-xs">
                <Mic className="w-4 h-4 mr-2" /> Voice Mock Interview
              </Button>
              <a href={interviewAPI.downloadPrepPDF(selectedJob.id)}
                target="_blank" rel="noopener noreferrer"
                data-testid="download-prep-pdf-btn"
                className="inline-flex items-center gap-2 bg-[#0D9488]/10 text-amber-400 border border-[#0D9488]/20 hover:bg-[#0D9488]/20 rounded-lg h-10 px-5 font-bold uppercase tracking-wider text-xs transition-colors">
                <Download className="w-4 h-4" /> PDF
              </a>
            </div>
          </div>

          <div className="p-6">
            {generating ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Generating interview prep...</p>
              </div>
            ) : prepContent ? (
              <ScrollArea className="max-h-[600px]">
                <div className="prose prose-invert prose-sm max-w-none prose-headings:text-purple-300 prose-strong:text-slate-200 prose-li:text-slate-300" style={{ color: 'var(--t-text-secondary)' }}>
                  <ReactMarkdown>{prepContent.response}</ReactMarkdown>
                </div>
              </ScrollArea>
            ) : (
              <p className="text-sm text-center py-8" style={{ color: 'var(--t-text-muted)' }}>Select a job above to generate interview prep</p>
            )}
          </div>
        </motion.div>
      )}

      {/* Company Intel */}
      {selectedJob && !generating && prepContent && (
        <CompanyIntel job={selectedJob} />
      )}
    </div>
  );
}
